import { useMemo, useState } from "react";

import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { ProductForm } from "@/modules/products/ProductForm";
import { useProducts, type Product } from "@/modules/products/fixtures";

const PRODUCT_STATUS: StatusMap = {
  active: { label: "ACTIVE", color: "var(--ok)" },
  beta: { label: "BETA", color: "var(--info)" },
  hidden: { label: "HIDDEN", color: "var(--warn)" },
  inactive: { label: "INACTIVE", color: "var(--t-mute)" },
};

const COLS: DataCol<Product>[] = [
  {
    key: "name", label: "Product", width: 200,
    render: (p) => p.name,
  },
  { key: "slug", label: "Slug", width: 170, mono: true, render: (p) => p.product_id },
  // product_code is DISPLAY ONLY — the slug is what goes on the wire and into
  // the audit log. Both sessions got this wrong once; the column says so.
  { key: "code", label: "Code", width: 90, mono: true, render: (p) => p.product_code },
  { key: "desc", label: "Description", width: 250, render: (p) => p.description ?? <span className="cap">—</span> },
  { key: "spaces", label: "Spaces", width: 80, render: (p) => p.spaces },
  { key: "companies", label: "Companies", width: 100, render: (p) => p.companies },
  { key: "status", label: "Status", width: 100, render: (p) => <StatusChip st={p.status} map={PRODUCT_STATUS} /> },
];

/** Products — what a company can be granted access to.
 *
 *  A product carries spaces, permission maps, reference files and tool
 *  releases; allocating one to a company is what lets its people sign in.
 */
export function ProductsPage() {
  const products = useProducts();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [mode, setMode] = useState<"idle" | "new">("idle");
  const [done, setDone] = useState("");

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (status && p.status !== status) return false;
      if (q && !p.name.toLowerCase().includes(q) && !p.product_id.includes(q)) return false;
      return true;
    });
  }, [products, search, status]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Products"
        desc="What a company can be granted access to. A product carries its spaces, its permission map, its reference files and its tool releases."
        right={mode === "idle"
          ? <button className="btn teal" onClick={() => { setMode("new"); setDone(""); }}>Add product</button>
          : <button className="btn ghost" onClick={() => setMode("idle")}>← Back</button>}
      />

      {mode === "new" && (
        <div className="panel panel-pad" style={{ marginBottom: 16 }}>
          <div className="section-label">New product</div>
          <ProductForm
            onCancel={() => setMode("idle")}
            onDone={(name) => { setDone(`${name} created`); setMode("idle"); }}
          />
        </div>
      )}
      {done && <div style={{ marginBottom: 10, fontSize: 12.5, color: "var(--ok)" }}>{done}</div>}

      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SearchInput placeholder="Search name / slug…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(PRODUCT_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label.toLowerCase()}</option>
          ))}
        </Select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>
          {rows.length} of {products.length}
        </span>
      </div>

      <DataTable
        rows={rows}
        cols={COLS}
        rowKey={(p) => p.product_id}
        stickyFirst
        empty={<div className="panel panel-pad cap">No products match these filters.</div>}
      />
    </div>
  );
}
